import type { TranslationKeys } from './types';

export const it: TranslationKeys = {
    // Navbar
    navbar: {
      schools: 'Scuole',
      howItWorks: 'Come funziona',
      aboutMe: 'Chi sono',
      pricing: 'Prezzi',
      faqs: 'FAQ',
      memories: 'Ricordi',
      myAccount: 'Il mio account',
    },
    // Home Page - HERO MAIN (H1, tagline, subtitle)
    home: {
      title: 'Trova le tue foto di surf in pochi secondi',
      tagline: 'Fotografia di surf a Lanzarote',
      subtitle: 'Carica una foto e ritrova i tuoi ricordi all\'istante, senza cercare tra centinaia di immagini',
      cta: 'Trova le mie foto',
      stats: {
        photos: 'Foto',
        schools: 'Scuole',
        surfers: 'Surfer felici',
      },
    },
    // Schools Page - SURF SCHOOLS
    schools: {
      title: 'Scuole di surf',
      subtitle: 'Seleziona la tua scuola di surf per trovare le tue foto',
      viewPhotos: 'Vedi foto',
      introTitle: 'La tua scuola, il tuo giorno, i tuoi ricordi',
      descriptionMain: 'Seleziona la scuola dove hai fatto la lezione e trova la tua sessione organizzata per data.',
      descriptionSecondary: 'Sessioni quotidiane di fotografia di surf a Famara e La Santa.',
      loading: 'Caricamento scuole...',
      apiUnavailable:
        'Impossibile raggiungere il server (timeout o errore). Mostriamo l\'elenco predefinito — riprova tra qualche minuto.',
      retry: 'Riprova',
      otherSchools: 'ALTRE SCUOLE',
      valueHighQuality: 'Foto di alta qualità',
      valueInstantDownload: 'Download immediato',
      valueSecurePayment: 'Pagamento sicuro',
    },
    // How It Works Page - PHOTO ACCESS (SELFIE)
    howItWorks: {
      title: 'Trova le tue foto in modo sicuro',
      subtitle: 'Carica un selfie così il nostro sistema identifica automaticamente solo le tue foto.',
      step1: {
        title: 'Scegli la tua scuola di surf',
        description: 'Seleziona la scuola dove hai fatto la lezione',
      },
      step2: {
        title: 'Seleziona la data della sessione',
        description: 'Scegli il giorno in cui hai surfato',
      },
      step3: {
        title: 'Carica un selfie',
        description: 'Il nostro sistema identificherà automaticamente solo le tue foto',
      },
      step4: {
        title: 'Visualizza le tue foto',
        description: 'Vedi tutte le foto in cui compare il tuo viso',
      },
      step5: {
        title: 'Aggiungi i tuoi ricordi',
        description: 'Seleziona le foto che vuoi conservare',
      },
      step6: {
        title: 'Paga in modo sicuro',
        description: 'Effettua il pagamento in modo rapido e sicuro',
      },
      step7: {
        title: 'Download immediato',
        description: 'Ottieni le tue foto all\'istante senza filigrana',
      },
      smartSearch: {
        title: 'Ricerca intelligente',
        description: 'Questo processo garantisce la tua privacy ed evita che altre persone accedano o acquistino foto che non sono loro.',
      },
      cta: 'Trova le tue foto',
    },
    // About Me Page - ABOUT ME
    aboutMe: {
      label: 'Chi sono',
      title: 'Catturare i tuoi migliori',
      titleHighlight: 'momenti in acqua',
      intro: 'Sono <strong>Germán Abreu</strong>, fotografo specializzato in surf e sport acquatici a Lanzarote. Vivo a La Santa e lavoro principalmente a Famara e in altri spot di surf dell\'isola.',
      paragraph1: 'Dopo anni a fotografare surfisti e allievi delle scuole, ho sempre riscontrato lo stesso problema: trovare le proprie foto tra centinaia di immagini era lento e poco pratico.',
      paragraph2: 'Così è nato <strong className="brand-name">Surf Photo Lanzarote</strong>, un modo semplice, veloce e sicuro per far sì che ogni persona trovi solo le proprie foto, senza ricerche infinite e rispettando sempre la privacy.',
      mission: 'Il mio obiettivo è che tu porti a casa un ricordo autentico della tua esperienza in acqua. Perché le onde passano… ma i ricordi restano.',
      stats: {
        experience: '+8 anni',
        experienceLabel: 'Esperienza',
        schools: '+7',
        schoolsLabel: 'Scuole partner',
      },
    },
    // Pricing Page
    pricing: {
      title: 'Prezzi',
      subtitle: 'Scegli l\'opzione più adatta a te',
      plan1: {
        title: 'Il tuo ricordo migliore',
        description: 'Un\'immagine singola in alta qualità',
        details: [
          'Download immediato',
          'Alta risoluzione',
          'Formato JPG',
          'Adatto alla stampa',
          'Senza filigrana',
        ],
      },
      plan2: {
        title: 'Tutti i ricordi della tua sessione',
        description: 'Tutte le foto della stessa persona nello stesso giorno.',
        details: [
          'Tutte le foto del giorno',
          'Download immediato',
          'Alta risoluzione',
          'Formato JPG',
          'Senza filigrana',
        ],
      },
      plan3: {
        title: 'Giorno aggiuntivo',
        description: 'Aggiungi un altro giorno al tuo pacchetto.',
        details: [
          'Tutte le foto del giorno extra',
          'Download immediato',
          'Alta risoluzione',
          'Senza filigrana',
        ],
      },
      plan4: {
        title: 'Persona aggiuntiva',
        description: 'Aggiungi le foto di un\'altra persona. Possono essere dello stesso giorno o di un giorno diverso.',
        details: [
          'Tutte le foto di un\'altra persona',
          'Download immediato',
          'Alta risoluzione',
          'Senza filigrana',
        ],
      },
      includes: 'Include',
      note: 'Tutti i prezzi includono il download immediato senza filigrana',
    },
    // FAQ Page
    faqs: {
      title: 'Domande frequenti',
      subtitle: 'Trova risposte alle domande più comuni su come funziona Surf Photo Lanzarote',
      contactText: 'Non trovi la risposta che cerchi?',
      contactLink: 'Contattaci',
      questions: [
        {
          q: 'Come trovo le mie foto?',
          a: 'Seleziona la tua scuola di surf, il giorno della sessione e carica un selfie. Il sito ti mostrerà solo le foto in cui compare il tuo viso in quel giorno.',
        },
        {
          q: 'Perché devo caricare un selfie?',
          a: 'Usiamo la tecnologia di riconoscimento facciale per identificare il tuo viso e mostrarti solo le tue foto, senza dover cercare tra centinaia di immagini.',
        },
        {
          q: 'Cosa succede al selfie che carico?',
          a: 'Il selfie viene usato solo per effettuare la ricerca di quel giorno e non viene archiviato né utilizzato per altri scopi.',
        },
        {
          q: 'Posso cercare foto di più giorni con un solo selfie?',
          a: 'No. Ogni ricerca viene effettuata per giorno. Se hai surfato in giorni diversi, devi caricare un selfie per ogni sessione per trovare le tue foto.',
        },
        {
          q: 'Cosa faccio se siamo in due o più persone?',
          a: 'Ogni persona deve caricare il proprio selfie per trovare le proprie foto. Puoi aggiungere le foto di ogni persona al carrello e continuare senza problemi.',
        },
        {
          q: 'Perché le foto hanno una filigrana?',
          a: 'Le foto vengono mostrate con filigrana per proteggere il lavoro del fotografo. Dopo l\'acquisto, potrai scaricarle senza filigrana.',
        },
        {
          q: 'Che qualità hanno le foto scaricate?',
          a: 'Le foto vengono scaricate in alta qualità, pronte per uso personale, social media e stampa.',
        },
        {
          q: 'Per quanto tempo saranno disponibili le mie foto?',
          a: 'Le foto restano online per un periodo limitato. Ti consigliamo di scaricarle il prima possibile dopo l\'acquisto.',
        },
        {
          q: 'Posso acquistare foto di più di un giorno?',
          a: 'Sì. Puoi accedere a giorni diversi e acquistare le foto di ogni sessione in modo indipendente.',
        },
        {
          q: 'Le foto sono pubbliche?',
          a: 'No. Le foto sono organizzate per scuola e giorno, così ogni cliente accede solo alla propria sessione.',
        },
        {
          q: 'Il pagamento è sicuro?',
          a: 'Sì. Tutti i pagamenti vengono elaborati tramite Stripe, una piattaforma di pagamento sicura e affidabile.',
        },
        {
          q: 'Cosa faccio se non mi trovo o ho un problema?',
          a: 'Se non compare nelle foto o hai qualsiasi problema, contattaci e ti aiuteremo.',
        },
      ],
    },
    // School Days Page
    schoolDays: {
      title: 'Seleziona un giorno',
      subtitle: 'Scegli il giorno della tua sessione di surf',
      noPhotos: 'Nessuna foto disponibile per questa scuola al momento.',
      back: 'Indietro',
      searchByDate: 'Cerca per data',
      search: 'Cerca',
      availableDays: 'Giorni disponibili',
      selectDaySubtitle: 'Seleziona un giorno per trovare le tue foto',
      loadingDays: 'Caricamento giorni disponibili...',
      noDaysYet: 'Nessun giorno disponibile per questa scuola al momento.',
      noDaysDesc: 'Le foto sono organizzate per giorno. Torna presto per nuove sessioni.',
      photos: 'foto',
      errorLoadingDays: 'Errore nel caricamento dei giorni disponibili',
      noPhotosForDate: 'Nessuna foto disponibile per quella data',
    },
    // Day Photos Page
    dayPhotos: {
      title: 'Le tue foto di surf',
      subtitle: 'Foto del',
      findTitle: 'Trova le tue foto',
      findSubtitle: 'Scegli se caricare un selfie o attivare la fotocamera per rilevare il tuo volto.',
      clickToSelect: 'Clicca per selezionare un selfie',
      orDragImage: 'o trascina un\'immagine qui',
      faceGuide: {
        hintStarting: 'Preparazione della guida facciale…',
        hintNoFace: 'Inquadra il viso dentro l’ovale',
        hintMultiple: 'Inquadra una sola persona',
        hintTooFar: 'Avvicinati un po’',
        hintTooClose: 'Allontanati un po’',
        hintMoveLeft: 'Spostati un po’ a sinistra',
        hintMoveRight: 'Spostati un po’ a destra',
        hintMoveUp: 'Alza un po’ il viso',
        hintMoveDown: 'Abbassa un po’ il viso',
        hintLookStraight: 'Guarda dritto in camera',
        turnLeft: 'Gira la testa a sinistra e tieni',
        turnRight: 'Ora gira la testa a destra',
        circleHead: 'Fai un cerchio lento con la testa',
        coverage: 'Viso {{pct}}%',
        hintHoldStill: 'Perfetto, resta fermo…',
        hintReady: 'Catturato',
        cameraDenied: 'Permesso fotocamera negato. Puoi caricare un selfie o riprovare.',
        choosePrompt: 'Come vuoi che troviamo il tuo volto?',
        uploadSelfie: 'Carica un selfie',
        uploadSelfieHint: 'Usa una foto che hai già',
        startCamera: 'Attiva la fotocamera',
        startCameraHint: 'Ti guidiamo per rilevare il tuo volto',
        backToChoice: 'Scegli un’altra opzione',
        capture: 'Scatta',
        useGallery: 'Carica dalla galleria',
        loadingDetector: 'Avvio della fotocamera…',
        galleryMultipleFaces: 'Carica un selfie con una sola persona (niente foto di gruppo).',
        galleryNoFace: 'Nessun volto rilevato. Prova con un altro selfie.',
      },
      changePhoto: 'Cambia foto',
      analyzing: 'Analisi in corso...',
      searchPhotos: 'Trova le mie foto',
      errorReadFile: 'Errore nella lettura del file. Prova con un\'altra immagine.',
      errorSelectFirst: 'Seleziona prima una foto',
      errorEmptyFile: 'Il file selezionato è vuoto. Seleziona un\'altra immagine.',
      errorAnalyze: 'Errore nell\'analisi della foto. Verifica che il server sia attivo.',
      yourSelfie: 'Il tuo selfie',
      analyzingFace: 'Analisi del tuo viso...',
      streamingMatchesSoFar: '{{count}} corrispondenze finora — ricerca in corso…',
      slowPathHint:
        'questa cartella non è ancora indicizzata, quindi confrontiamo foto per foto (lento solo la prima volta).',
      autoIndexHint:
        'Creazione dell\'indice in background — la prossima ricerca in questa cartella sarà molto più veloce.',
      indexClustersHint:
        'Creazione dell\'indice facciale (cluster)… avviene una volta per scuola/giorno — poi le ricerche usano solo i cluster.',
      searchDiagnostics:
        'Ricerca facciale: {{path}} · modalità {{mode}} · {{seconds}}s',
      searchingIn: 'Ricerca corrispondenze in',
      analysisResults: 'Risultati dell\'analisi',
      matches: 'Corrispondenze',
      photosAnalyzed: 'Foto analizzate',
      percentage: 'Percentuale',
      photosFound: 'Foto trovate',
      addAllMemories: 'Aggiungi tutti i tuoi ricordi',
      addAllMemoriesFor35: 'Aggiungi tutti i tuoi ricordi per {{price}}€',
      similarity: 'Similarità',
      buy: 'Acquista',
      viewLarge: 'Vedi a dimensione intera',
      noMatch: 'Nessuna corrispondenza',
      moreItems: 'altre',
      uploadPhoto: 'Carica la tua foto',
      uploadDescription: 'Carica una foto nitida del tuo viso per trovare le tue foto di surf',
      selectFile: 'Seleziona foto',
      findPhotos: 'Trova le mie foto',
      results: 'foto trovate',
      addToCart: 'Aggiungi al carrello',
      noResults: 'Nessuna foto trovata. Prova a caricare un\'altra foto.',
      savedSearchBanner: 'Mostrando la tua ricerca salvata — tutte le foto trovate nel tuo account.',
      newSearchLink: 'Esegui una nuova ricerca con un altro selfie',
      savedSearchLoading: 'Caricamento ricerca salvata...',
      savedSearchNotFound: 'Ricerca salvata non trovata. Puoi eseguire una nuova ricerca qui sotto.',
      partnerOffer: 'Offerta',
      partnerOfferCta: 'Vedi offerta',
      partnerOffersWhileSearching: 'Offerte partner mentre cerchiamo le tue foto',
    },
    // Checkout Success Page
    account: {
      loginTitle: 'Accedi',
      registerTitle: 'Crea il tuo profilo',
      loginLead: 'Accedi ai tuoi acquisti e alle ricerche salvate fino a 90 giorni.',
      continueGoogle: 'Continua con Google',
      orEmail: 'o con email',
      displayName: 'Nome',
      email: 'Email',
      password: 'Password',
      passwordStrengthLabel: 'Sicurezza password',
      strengthWeak: 'Debole',
      strengthFair: 'Discreta',
      strengthGood: 'Buona',
      strengthStrong: 'Forte',
      strengthHint: 'Usa maiuscole, numeri e simboli',
      signIn: 'Accedi',
      signUp: 'Crea account',
      forgotPassword: 'Password dimenticata?',
      forgotPasswordTitle: 'Reimposta la password',
      forgotPasswordLead: 'Inserisci la tua email e ti invieremo un link per scegliere una nuova password.',
      sendResetLink: 'Invia link',
      resetLinkSent: 'Se esiste un account con quella email, abbiamo inviato un link di reimpostazione.',
      resetLinkError: 'Impossibile inviare il link. Riprova.',
      backToSignIn: 'Torna all\'accesso',
      resetPasswordTitle: 'Scegli una nuova password',
      resetPasswordLead: 'Inserisci una nuova password per il tuo account.',
      newPassword: 'Nuova password',
      savePassword: 'Salva password',
      passwordResetSuccess: 'Password aggiornata. Ora puoi accedere.',
      passwordResetInvalid: 'Questo link di reimpostazione è scaduto o non è valido.',
      passwordResetError: 'Impossibile aggiornare la password. Riprova.',
      passwordMismatch: 'Le password non coincidono.',
      confirmPassword: 'Conferma password',
      noAccount: 'Non hai un account?',
      haveAccount: 'Hai già un account?',
      createAccount: 'Creane uno',
      photographerCta: 'Sei un fotografo?',
      photographerRegister: 'Crea account fotografo',
      photographerPanel: 'Pannello fotografo',
      verifyEmailSent: 'Controlla la tua email per verificare l\'account.',
      backHome: 'Torna alla home',
      oauthLoading: 'Accesso in corso...',
      oauthError: 'Accesso non riuscito. Riprova.',
      oauthMissing: 'Link di accesso non valido.',
      emailVerifiedTitle: 'Email verificata',
      emailVerifiedBody: 'Il tuo profilo è attivo. Puoi accedere e consultare la tua galleria.',
      goToAccount: 'Vai al mio account',
      emailInvalidTitle: 'Link non valido',
      emailInvalidBody: 'Il link di verifica è scaduto o non è valido.',
      checkEmailTitle: 'Controlla la tua email',
      checkEmailBody: 'Ti abbiamo inviato un link per verificare il tuo account.',
      checkEmailSpam: 'Se non lo vedi, controlla spam o promozioni.',
      resendVerification: 'Reinvia email di verifica',
      resendSuccess: 'Se l\'account esiste, abbiamo inviato una nuova email.',
      resendError: 'Impossibile reinviare l\'email. Riprova.',
      resendEmailRequired: 'Inserisci la tua email per reinviare.',
      emailNotConfigured:
        'Impossibile inviare l\'email dal server. Contatta l\'assistenza o prova l\'accesso con Google.',
      registerCreatedNoEmailTitle: 'Account creato',
      registerCreatedNoEmailBody:
        'Il tuo account è stato salvato, ma non siamo riusciti a inviare l\'email di verifica. Usa il reinvio qui sotto o accedi con Google.',
      galleryTitle: 'La mia galleria',
      profileSubtitle: 'I tuoi acquisti e ricerche salvate, fino a 90 giorni dopo ogni sessione.',
      tabProfile: 'Profilo',
      hello: 'Ciao',
      signOut: 'Esci',
      tabPurchases: 'Acquisti',
      tabSearches: 'Ricerche salvate',
      statPurchases: 'Acquisti',
      statSearches: 'Ricerche',
      statPhotos: 'Foto',
      editProfile: 'Modifica profilo',
      saveProfile: 'Salva modifiche',
      profileSaved: 'Profilo aggiornato',
      profileSaveError: 'Impossibile salvare il profilo',
      changePhoto: 'Cambia foto',
      removePhoto: 'Rimuovi foto',
      photoHint: 'JPG o PNG, max 5 MB. Usata come foto del profilo.',
      photoUploadError: 'Impossibile caricare la foto',
      photoRemoved: 'Foto rimossa',
      photoUploaded: 'Foto aggiornata',
      emailReadonly: 'L\'email non può essere modificata qui',
      authGoogle: 'Account Google',
      authEmail: 'Email e password',
      verified: 'Verificato',
      photosInOrder: 'foto',
      orderDate: 'Data ordine',
      downloadAvailable: 'Download disponibile',
      loadError: 'Impossibile caricare la galleria.',
      emailVerifyRequired:
        'Verifica la tua email per accedere alla galleria, agli acquisti e ai download.',
      verifyEmailLink: 'Verifica email',
      noPurchases: 'Non hai ancora acquisti.',
      noSearches: 'Nessuna ricerca salvata.',
      order: 'Ordine',
      downloadZip: 'Scarica ZIP',
      expired: 'Periodo di download terminato',
      newSearch: 'Trova foto di un altro giorno',
      photosFound: 'foto trovate',
      viewSession: 'Vedi sessione',
      searchSavedAt: 'Ricerca salvata',
      loading: 'Caricamento...',
    },
    profilePrompt: {
      beforePayTitle: 'Crea il tuo profilo',
      beforePayBody:
        'Accedi con Google o email per scaricare le tue foto in qualsiasi momento fino a 90 giorni dopo la data della sessione.',
      afterPayTitle: 'Salva le tue foto nel profilo',
      afterPayBody:
        'Senza profilo puoi scaricarle solo una volta adesso. Con un profilo accedi quando vuoi per 90 giorni.',
      exitTitle: 'Salva i tuoi risultati',
      exitBody: 'Crea un profilo per non dover caricare di nuovo il selfie la prossima volta.',
      createProfile: 'Crea profilo',
      saveToProfile: 'Salva nel mio profilo',
      saveResults: 'Salva risultati',
      continueGoogle: 'Continua con Google',
      notNow: 'Non ora',
      downloadNowOnly: 'Scarica solo adesso',
    },
    checkoutSuccess: {
      loading: 'Caricamento download...',
      paymentNotFound: 'Pagamento non trovato',
      noDownloadData: 'Nessun dato di download.',
      testHint: 'Per testare la sezione download senza pagare, visita:',
      backHome: 'Torna alla home',
      title: 'Pagamento completato!',
      subtitle: 'Grazie per l\'acquisto. Ecco le tue foto da scaricare senza filigrana.',
      hello: 'Ciao',
      downloads: 'Download',
      downloadsIntro: 'Puoi scaricare ogni foto in alta qualità cliccando sul pulsante o sull\'immagine.',
      downloadAll: 'Scarica tutte le foto',
      downloadingAll: 'Avvio download…',
      downloadThis: 'Scarica questa foto',
      downloadingOne: 'Download in corso…',
      downloadTitle: 'Scarica',
      total: 'Totale',
      photo: 'foto',
      photos: 'foto',
      exploreMore: 'Vedi altre scuole',
      missingSession: 'Manca session_id o modalità test (test=1).',
      paymentIncomplete:
        'Non siamo riusciti a caricare i dettagli del pagamento in questo momento, ma se hai completato il pagamento, è andato a buon fine — per favore non pagare di nuovo. Aggiorna questa pagina tra poco, oppure contattaci con la tua email e ti invieremo le foto.',
      loadError: 'Impossibile caricare i dettagli del pagamento.',
      unexpectedResponse: 'Risposta inattesa del server.',
      connectionError: 'Errore di connessione. Verifica che il backend sia attivo.',
      downloadsOnThisPage:
        'Le tue foto sono in questa pagina. Usa "Scarica tutte le foto" o ogni pulsante "Scarica questa foto" qui sotto.',
      guestProfileBenefit:
        'Puoi scaricare le tue foto qui in qualsiasi momento con questo link. Crea un profilo per salvarle nella galleria e accedervi quando vuoi.',
      createProfileCta: 'Crea profilo',
      goToGallery: 'La mia galleria',
      downloadFailed: 'Download non riuscito. Prova "Scarica tutte le foto" o aggiorna questa pagina.',
      voucherEmail:
        'Ti abbiamo anche inviato via email la conferma d\'acquisto con i voucher partner in PDF.',
    },
    checkoutCancel: {
      title: 'Pagamento annullato',
      message: 'Nessun addebito effettuato. Puoi tornare al carrello o continuare a navigare.',
      backHome: 'Torna alla home',
      viewSchools: 'Vedi scuole',
    },
    // Cart Modal
    cart: {
      title: 'I tuoi ricordi',
      item: 'articolo',
      items: 'articoli',
      empty: 'Il tuo carrello è vuoto',
      emptyDesc: 'Esplora le nostre foto e aggiungi i tuoi ricordi preferiti',
      explorePhotos: 'Esplora foto',
      total: 'Totale',
      checkout: 'Procedi al pagamento',
      remove: 'Rimuovi',
      beforePaymentDownloadNotice:
        'Le tue foto saranno disponibili per il download in questa stessa pagina subito dopo il pagamento',
      emailPlaceholder: 'La tua email',
      namePlaceholder: 'Il tuo nome',
      subtotal: 'Subtotale',
      igic: 'IGIC ({rate}%)',
      taxesNotIncludedNote: 'Prezzi IVA esclusa',
      discount: 'Sconto',
      processing: 'Elaborazione...',
      proceedToPayment: 'Procedi al pagamento',
      continueShopping: 'Continua lo shopping',
      securePayment: 'Pagamento sicuro',
      instantDownload: 'Download immediato',
      closeCart: 'Chiudi carrello',
      removeItem: 'Rimuovi articolo',
      packComplete: 'Pacchetto completo',
      photo: 'foto',
      photos: 'foto',
      digitalFormat: 'Formato digitale',
      highResolution: 'Alta risoluzione',
      errorEmailNameRequired: 'Inserisci email e nome per continuare',
      errorInvalidEmail: 'Inserisci un\'email valida (es. tu@email.com)',
      errorEmptyCart: 'Il carrello è vuoto',
      errorCheckoutSession: 'Impossibile creare la sessione di pagamento',
      errorNoPaymentUrl: 'URL di pagamento non ricevuta. Verifica che Stripe sia configurato.',
      errorPaymentFailed: 'Errore nell\'elaborazione del pagamento',
    },
    // Sticky purchase flow pricing bar
    purchasePricingBar: {
      ariaLabel: 'Prezzi',
      disclaimer:
        'Lo sconto sul 2° pacchetto si applica quando entrambi i pacchetti vengono aggiunti al carrello insieme. Prezzi IVA esclusa (IGIC).',
      blocks: [
        {
          description: 'La tua foto migliore',
          badge: '1 foto',
        },
        {
          description: 'Tutte le tue foto di una sessione',
          badge: 'Pacchetto completo',
        },
        {
          description: 'Sessione extra per te',
          badge: '2° pacchetto',
        },
        {
          description: 'Tutte le foto di un amico o familiare',
          badge: '2° pacchetto',
        },
      ],
    },
    // Partners
    partners: {
      title: 'Scuole partner',
    },
    // Footer
    footer: {
      brand: 'Surf Photo Lanzarote',
      description: 'Marketplace premium di fotografia di surf a Lanzarote',
      navigation: 'Navigazione',
      home: 'Home',
      schools: 'Scuole',
      howItWorks: 'Come funziona',
      pricing: 'Prezzi',
      legal: 'Legale',
      legalNotice: 'Note legali',
      privacy: 'Informativa sulla privacy',
      terms: 'Termini e condizioni',
      contact: 'Contatto',
      email: 'info@surfphotolanzarote.com',
      location: 'Lanzarote, Isole Canarie',
      rights: 'Tutti i diritti riservati',
    },
    // Common
    common: {
      loading: 'Caricamento...',
      error: 'Si è verificato un errore',
      close: 'Chiudi',
      cancel: 'Annulla',
      confirm: 'Conferma',
      save: 'Salva',
      delete: 'Elimina',
      edit: 'Modifica',
      back: 'Indietro',
    },
  };
